import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import {
  ArrowLeft, Clock, Users, Play, ChevronDown, ChevronUp,
  Globe, Award, Calendar, BarChart3, Check, BookOpen, ShieldCheck
} from 'lucide-react';
import CourseReviewsSection from './CourseReviewsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function formatDuration(seconds) {
  if (!seconds) return '0 min';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) return `${hrs} hr ${mins > 0 ? `${mins} min` : ''}`;
  if (mins > 0) return `${mins} min`;
  return `${secs} sec`;
}

function formatDurationShort(seconds) {
  if (!seconds) return '';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs} hr${mins > 0 ? ` ${mins} min` : ''}`;
  if (mins > 0) return `${mins} min`;
  return `${seconds} sec`;
}

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({ user_name: '', user_email: '', user_phone: '' });
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [showFullAbout, setShowFullAbout] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const res = await fetch(`${API_URL}/api/courses/public/${courseId}`);
      if (!res.ok) throw new Error('Course not found');
      const data = await res.json();
      setCourse(data.course);
      // Auto-expand first section
      if (data.course?.sections?.length > 0) {
        setExpandedSections({ [data.course.sections[0].id]: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const totalLessons = course?.sections?.reduce((acc, s) => acc + (s.lessons?.length || 0), 0) || 0;
  const totalDuration = course?.sections?.reduce((acc, s) => acc + (s.total_duration || 0), 0) || 0;

  const handlePurchase = async () => {
    if (!purchaseForm.user_name || !purchaseForm.user_email || !purchaseForm.user_phone) {
      setPurchaseError('Please fill all fields');
      return;
    }
    if (purchaseForm.user_phone.length < 10) {
      setPurchaseError('Please enter a valid phone number');
      return;
    }
    setPurchasing(true);
    setPurchaseError('');
    try {
      const res = await fetch(`${API_URL}/api/courses/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: courseId,
          user_name: purchaseForm.user_name,
          user_email: purchaseForm.user_email,
          user_phone: purchaseForm.user_phone
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Purchase failed');

      // Open Razorpay
      const options = {
        key: data.razorpay_key_id,
        amount: data.amount * 100,
        currency: data.currency || 'INR',
        name: 'Upper Circuit Stocks',
        description: data.course_title || course.title,
        order_id: data.order_id,
        prefill: {
          name: purchaseForm.user_name,
          email: purchaseForm.user_email,
          contact: purchaseForm.user_phone
        },
        theme: { color: '#D4AF37' },
        handler: async function (response) {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API_URL}/api/courses/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            const verifyData = await verifyRes.json();
            if (verifyData.status === 'success') {
              alert('Payment successful! Login to access your course.');
              navigate('/login');
            } else {
              setPurchaseError('Payment verification failed. Contact support.');
            }
          } catch (e) {
            setPurchaseError('Payment verification error. Contact support.');
          }
        },
        modal: {
          ondismiss: function () {
            setPurchasing(false);
          }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setPurchaseError(err.message);
    } finally {
      setPurchasing(false);
    }
  };

  const getThumbnailUrl = (path) => {
    if (!path) return null;
    return `${API_URL}/api/courses/files/${path}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-golden border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center">
        <BookOpen className="w-16 h-16 text-gray-600 mb-4" />
        <h2 className="text-xl mb-4">Course not found</h2>
        <Button onClick={() => navigate('/courses')} className="bg-golden text-black hover:bg-golden/90">
          Browse Courses
        </Button>
      </div>
    );
  }

  const discountPercent = course.original_price > course.price
    ? Math.round((1 - course.price / course.original_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/90 backdrop-blur-sm z-50 border-b border-golden/30">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/courses')} className="flex items-center gap-2 text-golden hover:text-golden-light transition-colors" data-testid="back-courses-btn">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Courses</span>
            </button>
          </div>
          <a href="/login" className="bg-golden hover:bg-golden/90 text-black font-semibold px-4 py-2 rounded-lg transition-colors text-sm" data-testid="login-btn">
            Login
          </a>
        </div>
      </header>

      <div className="pt-20 pb-16">
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="breadcrumb">
            <button onClick={() => navigate('/')} className="hover:text-golden transition-colors">Home</button>
            <span>/</span>
            <button onClick={() => navigate('/courses')} className="hover:text-golden transition-colors">Courses</button>
            <span>/</span>
            <span className="text-golden truncate">{course.title}</span>
          </div>
        </div>

        {/* Course Header */}
        <div className="container mx-auto px-4">
          {course.category_name && (
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-3 uppercase text-xs tracking-wider" data-testid="course-category">
              {course.category_name}
            </Badge>
          )}
          <h1 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="course-title">{course.title}</h1>
        </div>

        {/* Main Content Grid */}
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Course content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Thumbnail / Trailer */}
              <div className="rounded-xl overflow-hidden bg-gradient-to-br from-golden/20 to-golden/5 aspect-video relative" data-testid="course-thumbnail">
                {course.has_trailer ? (
                  <video
                    src={`${API_URL}/api/courses/trailer/${course.id}`}
                    controls
                    poster={course.thumbnail_path ? getThumbnailUrl(course.thumbnail_path) : undefined}
                    controlsList="nodownload"
                    className="w-full h-full object-cover"
                    data-testid="course-trailer"
                  />
                ) : course.thumbnail_path ? (
                  <img src={getThumbnailUrl(course.thumbnail_path)} alt={course.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="w-20 h-20 text-golden/40" />
                  </div>
                )}
              </div>

              {/* What You Will Learn */}
              {course.what_you_learn?.length > 0 && (
                <Card className="bg-white/5 border-golden/20" data-testid="what-you-learn">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-white mb-4">What You Will Learn</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {course.what_you_learn.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-golden mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Course Overview - Modules & Sub-Modules */}
              <div data-testid="course-overview">
                <h2 className="text-xl font-bold text-white mb-2">Course Curriculum</h2>
                <p className="text-gray-400 text-sm mb-4">
                  {course.sections?.length || 0} modules &bull; {totalLessons} topics &bull; {formatDuration(totalDuration)} content
                </p>
                <div className="space-y-2">
                  {course.sections?.map((section, idx) => (
                    <div key={section.id} className="border border-golden/20 rounded-lg overflow-hidden" data-testid={`section-${idx}`}>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className={`w-full flex items-center justify-between p-4 transition-colors ${
                          expandedSections[section.id] ? 'bg-golden/10' : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-3 text-left">
                          <span className="text-golden/60 text-xs font-mono w-6">{String(idx + 1).padStart(2, '0')}</span>
                          <span className="text-white font-medium">{section.title}</span>
                          {section.has_video && <Play className="w-3.5 h-3.5 text-golden" />}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-golden text-sm">{formatDurationShort(section.total_duration)}</span>
                          {expandedSections[section.id] ? (
                            <ChevronUp className="w-5 h-5 text-golden" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>
                      {expandedSections[section.id] && section.lessons?.length > 0 && (
                        <div className="border-t border-golden/10">
                          {/* Table header */}
                          <div className="flex items-center px-6 py-2 bg-white/3 text-[11px] text-gray-500 uppercase tracking-wider border-b border-white/5">
                            <span className="w-10">#</span>
                            <span className="flex-1">Sub Module</span>
                            <span className="w-20 text-right">Duration</span>
                          </div>
                          {section.lessons.map((lesson, lIdx) => (
                            <div key={lesson.id} className="flex items-center gap-3 px-6 py-3 border-b border-white/5 last:border-0 hover:bg-white/3 transition-colors group" data-testid={`lesson-row-${idx}-${lIdx}`}>
                              <span className="text-gray-600 text-xs font-mono w-10">{idx + 1}.{lIdx + 1}</span>
                              <Play className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                              <button
                                onClick={() => navigate(`/courses/${courseId}/lesson/${lesson.id}`)}
                                className="text-gray-300 text-sm flex-1 text-left hover:text-golden transition-colors line-clamp-1"
                                data-testid={`lesson-share-link-${lesson.id}`}
                                title="Watch free 30s preview"
                              >
                                {lesson.title}
                              </button>
                              <span className="text-[10px] text-amber-400/80 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:inline">Preview</span>
                              {lesson.duration_seconds > 0 && (
                                <span className="text-gray-500 text-xs w-20 text-right">{formatDurationShort(lesson.duration_seconds)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* About The Course */}
              {course.about && (
                <div data-testid="about-course">
                  <h2 className="text-xl font-bold text-white mb-3">About The Course</h2>
                  <p className={`text-gray-400 leading-relaxed ${!showFullAbout ? 'line-clamp-4' : ''}`}>
                    {course.about}
                  </p>
                  {course.about.length > 300 && (
                    <button
                      onClick={() => setShowFullAbout(!showFullAbout)}
                      className="text-golden text-sm mt-2 hover:underline"
                    >
                      {showFullAbout ? 'show less' : 'show more'}
                    </button>
                  )}
                </div>
              )}

              {/* Student Reviews (only renders if approved reviews exist) */}
              <CourseReviewsSection courseId={courseId} />

              {/* External Links */}
              {course.external_links?.length > 0 && (
                <Card className="bg-white/5 border-golden/20" data-testid="external-links">
                  <CardContent className="p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-golden" /> Useful Links
                    </h2>
                    <div className="space-y-2">
                      {course.external_links.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                        >
                          <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                          {link.title || link.url}
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right: Purchase Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <Card className="bg-white/5 border-golden/20" data-testid="purchase-card">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 text-gray-300">
                        <Users className="w-5 h-5 text-golden" />
                        <span className="text-sm"><strong>{course.enrolled_count || 0}</strong> Learners enrolled</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <BarChart3 className="w-5 h-5 text-golden" />
                        <span className="text-sm">{course.level}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <Clock className="w-5 h-5 text-golden" />
                        <span className="text-sm">{formatDuration(totalDuration)} of Content</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <Globe className="w-5 h-5 text-golden" />
                        <span className="text-sm">{course.language}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <Calendar className="w-5 h-5 text-golden" />
                        <span className="text-sm">{course.validity_days} Day Course Access</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-300">
                        <ShieldCheck className="w-5 h-5 text-golden" />
                        <span className="text-sm">Secure Video Access</span>
                      </div>
                    </div>

                    <Separator className="bg-golden/20" />

                    {/* Price */}
                    <div>
                      <div className="flex items-baseline gap-3">
                        <span className="text-3xl font-bold text-golden" data-testid="course-price">
                          {course.price > 0 ? `₹${course.price.toLocaleString()}` : 'Free'}
                        </span>
                        {course.original_price > course.price && (
                          <>
                            <span className="text-gray-500 line-through text-lg">₹{course.original_price.toLocaleString()}</span>
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              SAVE {discountPercent}%
                            </Badge>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Purchase Form / Button */}
                    {!showPurchaseForm ? (
                      <Button
                        onClick={() => setShowPurchaseForm(true)}
                        className="w-full bg-golden hover:bg-golden/90 text-black font-bold text-lg py-6"
                        data-testid="buy-now-btn"
                      >
                        Buy Now
                      </Button>
                    ) : (
                      <div className="space-y-3" data-testid="purchase-form">
                        <input
                          type="text"
                          placeholder="Full Name"
                          value={purchaseForm.user_name}
                          onChange={e => setPurchaseForm(prev => ({ ...prev, user_name: e.target.value }))}
                          className="w-full px-3 py-2 bg-white/10 border border-golden/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-golden"
                          data-testid="purchase-name"
                        />
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={purchaseForm.user_email}
                          onChange={e => setPurchaseForm(prev => ({ ...prev, user_email: e.target.value }))}
                          className="w-full px-3 py-2 bg-white/10 border border-golden/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-golden"
                          data-testid="purchase-email"
                        />
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          value={purchaseForm.user_phone}
                          onChange={e => setPurchaseForm(prev => ({ ...prev, user_phone: e.target.value }))}
                          className="w-full px-3 py-2 bg-white/10 border border-golden/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-golden"
                          data-testid="purchase-phone"
                        />
                        {purchaseError && (
                          <p className="text-red-400 text-sm" data-testid="purchase-error">{purchaseError}</p>
                        )}
                        <Button
                          onClick={handlePurchase}
                          disabled={purchasing}
                          className="w-full bg-golden hover:bg-golden/90 text-black font-bold py-5"
                          data-testid="confirm-purchase-btn"
                        >
                          {purchasing ? 'Processing...' : `Pay ₹${course.price?.toLocaleString()}`}
                        </Button>
                        <button
                          onClick={() => { setShowPurchaseForm(false); setPurchaseError(''); }}
                          className="w-full text-gray-400 text-sm hover:text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-golden/20 py-8 px-4 bg-black/50">
        <div className="container mx-auto text-center">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Upper Circuit Stocks. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CourseDetailPage;
